import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { BookOpen, Award, Users, ArrowRight, Search, X, Clock } from "lucide-react";

export default function Landing() {
    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [subject, setSubject] = useState("all");
    const [level, setLevel] = useState("all");

    useEffect(() => {
        api.get("/programs")
            .then((r) => {
                const data = r.data;
                setPrograms(
                    Array.isArray(data)
                        ? data
                        : Array.isArray(data?.programs)
                            ? data.programs
                            : Array.isArray(data?.data)
                                ? data.data
                                : []
                );
            })
            .catch(() => setPrograms([]))
            .finally(() => setLoading(false));
    }, []);

    const subjects = useMemo(() => {
        const s = new Set(programs.map((p) => p.subject).filter(Boolean));
        return Array.from(s).sort();
    }, [programs]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return programs.filter((p) => {
            if (subject !== "all" && p.subject !== subject) return false;
            if (level !== "all" && p.level !== level) return false;
            if (
                q &&
                !p.title.toLowerCase().includes(q) &&
                !(p.description || "").toLowerCase().includes(q) &&
                !(p.subject || "").toLowerCase().includes(q)
            )
                return false;
            return true;
        });
    }, [programs, query, subject, level]);

    const clearFilters = () => {
        setQuery("");
        setSubject("all");
        setLevel("all");
    };
    const activeFilterCount =
        (query.trim() ? 1 : 0) + (subject !== "all" ? 1 : 0) + (level !== "all" ? 1 : 0);

    return (
        <div data-testid="landing-page">
            {/* Hero */}
            <section className="border-b border-border bg-gradient-to-b from-secondary/40 to-background">
                <div className="max-w-7xl mx-auto px-6 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-7 space-y-6 animate-fade-in">
                        <div className="text-xs uppercase tracking-[0.25em] text-accent font-semibold">
                            Education That Leads to Careers · Cambridge College Online
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-primary leading-[1.05]">
                            Your Gateway to<br />
                            <span className="italic">Global Success.</span>
                        </h1>
                        <p className="text-base md:text-lg text-foreground/75 max-w-xl leading-relaxed">
                            Join Cambridge College Online and gain industry-ready skills through
                            practical, career-focused diploma and certificate programs — delivered
                            fully online from British Columbia.
                        </p>
                        <div className="flex flex-wrap gap-3 pt-2">
                            <Button
                                size="lg"
                                className="bg-primary hover:bg-primary/90"
                                data-testid="hero-browse-programs"
                                onClick={() =>
                                    document
                                        .getElementById("catalog")
                                        ?.scrollIntoView({ behavior: "smooth" })
                                }
                            >
                                Browse Programs
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                            <Button
                                size="lg"
                                variant="outline"
                                data-testid="hero-sign-in"
                                asChild
                            >
                                <Link to="/login">I have an account</Link>
                            </Button>
                        </div>
                    </div>
                    <div className="md:col-span-5">
                        <div className="relative">
                            <img
                                src="https://images.unsplash.com/photo-1559511260-66a654ae982a?auto=format&fit=crop&w=1200&q=80"
                                alt="Vancouver, British Columbia waterfront — Cambridge College Online"
                                className="w-full h-[420px] object-cover rounded-sm border border-border shadow-[0_20px_60px_-20px_rgba(44,95,141,0.35)]"
                            />
                            <div className="absolute -bottom-4 -left-4 bg-card border border-border rounded-sm p-4 w-56 shadow-sm hidden md:block">
                                <div className="text-2xl font-display text-primary">12,400+</div>
                                <div className="text-xs text-muted-foreground uppercase tracking-wider">
                                    Learners enrolled
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                    {
                        icon: BookOpen,
                        title: "Expert curriculum",
                        body: "Programs shaped by instructors and aligned to Canadian educational standards.",
                    },
                    {
                        icon: Award,
                        title: "Branded certificate",
                        body: "Complete your program to receive a verifiable, branded PDF certificate.",
                    },
                    {
                        icon: Users,
                        title: "Learn in community",
                        body: "Discuss lessons with peers and receive feedback from subject-matter experts.",
                    },
                ].map((v) => (
                    <Card key={v.title} className="border-border">
                        <CardContent className="p-8">
                            <v.icon className="h-6 w-6 text-accent" />
                            <h3 className="font-display text-xl text-primary mt-4">{v.title}</h3>
                            <p className="text-sm text-foreground/70 mt-2 leading-relaxed">
                                {v.body}
                            </p>
                        </CardContent>
                    </Card>
                ))}
            </section>

            {/* Catalog */}
            <section id="catalog" className="max-w-7xl mx-auto px-6 py-12">
                <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">
                            Catalog
                        </div>
                        <h2 className="font-display text-3xl md:text-4xl text-primary mt-2">
                            Current Programs
                        </h2>
                    </div>
                    <div className="text-sm text-muted-foreground">
                        Showing {filtered.length} of {programs.length}
                    </div>
                </div>

                {/* Search + Filters */}
                <div className="mb-8 grid grid-cols-1 md:grid-cols-[1fr_180px_180px_auto] gap-3" data-testid="catalog-filters">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search by title, description, or subject…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            className="pl-9"
                            data-testid="catalog-search-input"
                        />
                    </div>
                    <Select value={subject} onValueChange={setSubject}>
                        <SelectTrigger data-testid="catalog-subject-select">
                            <SelectValue placeholder="Subject" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All subjects</SelectItem>
                            {subjects.map((s) => (
                                <SelectItem key={s} value={s} className="capitalize">
                                    {s}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={level} onValueChange={setLevel}>
                        <SelectTrigger data-testid="catalog-level-select">
                            <SelectValue placeholder="Level" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All levels</SelectItem>
                            <SelectItem value="beginner">Beginner</SelectItem>
                            <SelectItem value="intermediate">Intermediate</SelectItem>
                            <SelectItem value="advanced">Advanced</SelectItem>
                        </SelectContent>
                    </Select>
                    {activeFilterCount > 0 ? (
                        <Button
                            variant="ghost"
                            onClick={clearFilters}
                            data-testid="catalog-clear-filters"
                        >
                            <X className="h-4 w-4 mr-1" />
                            Clear {activeFilterCount}
                        </Button>
                    ) : (
                        <div />
                    )}
                </div>

                {loading ? (
                    <div className="py-20 text-center text-muted-foreground">Loading catalog…</div>
                ) : filtered.length === 0 ? (
                    <div className="py-20 text-center text-muted-foreground border border-dashed border-border rounded-sm">
                        {programs.length === 0
                            ? "No programs currently offered."
                            : "No programs match those filters."}
                        {programs.length > 0 && activeFilterCount > 0 && (
                            <div className="mt-3">
                                <Button variant="outline" size="sm" onClick={clearFilters}>
                                    Reset filters
                                </Button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="programs-grid">
                        {filtered.map((p) => (
                            <Link
                                key={p.id}
                                to={`/programs/${p.id}`}
                                data-testid={`program-card-${p.id}`}
                                className="group"
                            >
                                <Card className="overflow-hidden border-border transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(19,75,48,0.08)]">
                                    <div className="aspect-[16/9] bg-muted overflow-hidden">
                                        <img
                                            src={
                                                p.thumbnail_url ||
                                                "https://images.pexels.com/photos/8199765/pexels-photo-8199765.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                                            }
                                            alt={p.title}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                                        />
                                    </div>
                                    <CardContent className="p-6">
                                        <div className="flex items-center gap-2 mb-3">
                                            <Badge
                                                variant="outline"
                                                className="capitalize border-border text-muted-foreground"
                                            >
                                                {p.subject}
                                            </Badge>
                                            <Badge
                                                variant="outline"
                                                className="capitalize border-border text-muted-foreground"
                                            >
                                                {p.level}
                                            </Badge>
                                        </div>
                                        <h3 className="font-display text-xl text-primary leading-snug">
                                            {p.title}
                                        </h3>
                                        <p className="text-sm text-foreground/70 mt-2 line-clamp-2">
                                            {p.description}
                                        </p>
                                        {(p.total_hours || p.total_chapters) ? (
                                            <div className="text-xs text-muted-foreground mt-3 flex items-center gap-2">
                                                <Clock className="h-3.5 w-3.5" />
                                                {p.total_hours
                                                    ? `${Number(p.total_hours).toFixed(1)} hr${p.total_hours === 1 ? "" : "s"}`
                                                    : ""}
                                                {p.total_hours && p.total_chapters ? " · " : ""}
                                                {p.total_chapters
                                                    ? `${p.total_chapters} chapter${p.total_chapters === 1 ? "" : "s"}`
                                                    : ""}
                                            </div>
                                        ) : null}
                                        <div className="flex items-end justify-between mt-6 pt-4 border-t border-border">
                                            <div>
                                                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                                                    Tuition
                                                </div>
                                                <div className="font-display text-2xl text-primary leading-tight">
                                                    ${p.price.toFixed(2)}
                                                    <span className="text-xs text-muted-foreground ml-1 font-sans">
                                                        CAD · domestic
                                                    </span>
                                                </div>
                                                {p.price_international != null
                                                && Number(p.price_international) > 0 ? (
                                                    <div className="text-sm text-foreground/70 mt-0.5">
                                                        ${Number(p.price_international).toFixed(2)}
                                                        <span className="text-xs text-muted-foreground ml-1">
                                                            CAD · international
                                                        </span>
                                                    </div>
                                                ) : null}
                                            </div>
                                            <span className="text-sm text-accent font-medium">
                                                View details →
                                            </span>
                                        </div>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}